import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  TrendingUp, TrendingDown, Flame, Snowflake, Trophy,
  Activity, ArrowUpRight, Sparkles, ShieldCheck, ArrowLeft, ExternalLink,
} from 'lucide-react';
import { BOT_CONFIG } from './MTModule';
import { apiFetch } from '../utils/apiFetch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_SRC = process.env.REACT_APP_LOGO_URL;

const fmtUsdShort = (n) => {
  const v = Number(n) || 0;
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const fmtNum = (n, d = 2) => (Number(n) || 0).toFixed(d);

/* ─────────────────────── LONG/SHORT DONUT ─────────────────────── */
const LongShortDonut = ({ analytics, color, glow }) => {
  const long  = analytics?.longTrades || 0;
  const short = analytics?.shortTrades || 0;
  const total = long + short;
  if (total === 0) return null;

  const longPct  = (long / total) * 100;
  const shortPct = (short / total) * 100;
  const circ = 2 * Math.PI * 32; // r=32
  const longLen = (longPct / 100) * circ;

  const longWonPct  = analytics?.longWonTradesPercent ?? (analytics?.wonLongTradesPercent || 0);
  const shortWonPct = analytics?.shortWonTradesPercent ?? (analytics?.wonShortTradesPercent || 0);

  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 80 80" width="80" height="80">
        <circle cx="40" cy="40" r="32" fill="none" stroke="#ef4444" strokeWidth="10" strokeOpacity="0.85" />
        <circle cx="40" cy="40" r="32" fill="none" stroke="#22c55e" strokeWidth="10"
          strokeDasharray={`${longLen} ${circ}`}
          strokeDashoffset={circ * 0.25}
          transform="rotate(-90 40 40)"
          style={{ filter: 'drop-shadow(0 0 6px rgba(34,197,94,0.6))' }} />
        <text x="40" y="44" textAnchor="middle" fontSize="11" fontWeight="900" fill="#fff" fontFamily="ui-monospace, monospace">
          {total}
        </text>
        <text x="40" y="54" textAnchor="middle" fontSize="6" fill="#9EB4C0">TRADES</text>
      </svg>
      <div className="text-[11px] space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#22c55e]" />
          <span className="text-white font-bold">BUY {fmtPct(longPct)}</span>
          {longWonPct > 0 && <span className="text-[#9EB4C0]">· {fmtPct(longWonPct)} WR</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#ef4444]" />
          <span className="text-white font-bold">SELL {fmtPct(shortPct)}</span>
          {shortWonPct > 0 && <span className="text-[#9EB4C0]">· {fmtPct(shortWonPct)} WR</span>}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── STREAK BANNER ─────────────────────── */
const StreakBanner = ({ analytics, color, glow }) => {
  const winStreak  = analytics?.longestWinningStreak || 0;
  const lossStreak = analytics?.longestLosingStreak || 0;
  if (winStreak === 0 && lossStreak === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-lg p-2 flex items-center gap-2"
        style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.30)' }}>
        <Flame className="w-4 h-4 text-[#22c55e]" />
        <div className="leading-tight">
          <div className="text-[8px] tracking-[0.2em] uppercase text-[#9EB4C0]">Win streak</div>
          <div className="text-sm font-black text-[#22c55e] font-mono">{winStreak}</div>
        </div>
      </div>
      <div className="rounded-lg p-2 flex items-center gap-2"
        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.30)' }}>
        <Snowflake className="w-4 h-4 text-[#ef4444]" />
        <div className="leading-tight">
          <div className="text-[8px] tracking-[0.2em] uppercase text-[#9EB4C0]">Loss streak</div>
          <div className="text-sm font-black text-[#ef4444] font-mono">{lossStreak}</div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── EQUITY CURVE ─────────────────────── */
// Deterministic preview curve (until MetaStats trades history is wired)
const seedHash = (s) => { let h = 0; const str = String(s || ''); for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0; return Math.abs(h); };
const mulb = (a) => () => { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };

const EquityCurve = ({ analytics, login, color, glow, animateIn }) => {
  const profit = analytics?.profit || 0;
  // Will be replaced by real MetaStats `equityChart` once available
  const points = useMemo(() => {
    const n = 60;
    const rand = mulb(seedHash(login || 'x'));
    const data = [];
    let v = 0;
    for (let i = 0; i < n - 1; i++) {
      v += (rand() - (profit >= 0 ? 0.35 : 0.65)) * (Math.abs(profit) || 1000) / 20;
      data.push(v);
    }
    data.push(profit);
    const min = Math.min(0, ...data);
    const max = Math.max(0, ...data);
    const range = (max - min) || 1;
    return data.map((d, i) => ({
      x: (i / (n - 1)) * 100,
      y: 100 - ((d - min) / range) * 100,
      v: d,
    }));
  }, [profit, login]);

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaD = pathD + ` L 100 100 L 0 100 Z`;
  const finalColor = profit >= 0 ? color : '#ef4444';

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-24">
      <defs>
        <linearGradient id={`eq-grad-${login}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={finalColor} stopOpacity="0.45" />
          <stop offset="100%" stopColor={finalColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#eq-grad-${login})`} />
      <path d={pathD} fill="none" stroke={finalColor} strokeWidth="1.2" vectorEffect="non-scaling-stroke"
        style={{
          filter: `drop-shadow(0 0 4px ${glow})`,
          strokeDasharray: 300,
          strokeDashoffset: animateIn ? 0 : 300,
          transition: 'stroke-dashoffset 1.8s ease-out',
        }} />
    </svg>
  );
};

/* ─────────────────────── BOT CARD ─────────────────────── */
const fmtAuditDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const BotHubCard = ({ bot, analytics, login, server, mult, animateIn, auditStartDate, myfxbookUrl }) => {
  const cfg = BOT_CONFIG[bot] || { color: '#9EB4C0', glow: 'rgba(255,255,255,0.3)', label: bot, logo: null };
  const trades = analytics?.trades || 0;
  const winRate = analytics?.wonTradesPercent || (trades > 0 ? (analytics?.wonTrades / trades) * 100 : 0);
  const pf = analytics?.profitFactor || 0;
  const pfAllWins = !!analytics?._pf_all_wins;
  const pfPending = !!analytics?._pf_pending;
  const sharpe = analytics?.sharpeRatio || 0;
  const sharpePending = !!analytics?._sharpe_pending;
  const dd = analytics?.maxDrawdownPercent || analytics?.maxDrawdown || 0;
  const ddPending = !!analytics?._dd_pending;
  const profit = analytics?.profit || 0;
  const monthlyGain = analytics?.monthlyGain || 0;
  const auditLabel = fmtAuditDate(auditStartDate);

  const ready = trades > 0;

  return (
    <Card
      className="relative overflow-hidden border"
      style={{
        background: `linear-gradient(165deg, ${cfg.color}15 0%, rgba(4,8,22,0.92) 60%)`,
        borderColor: `${cfg.color}55`,
        boxShadow: `0 14px 40px -20px ${cfg.glow}`,
      }}
      data-testid={`bot-hub-card-${bot}`}
    >
      {/* Scan lines */}
      <div className="absolute inset-0 pointer-events-none opacity-25 mix-blend-overlay"
        style={{ backgroundImage: 'repeating-linear-gradient(180deg, transparent 0 2px, rgba(255,255,255,0.04) 2px 3px)' }} />
      <CardContent className="p-5 relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
            style={{
              background: `radial-gradient(circle, ${cfg.color}25, ${cfg.color}05)`,
              border: `2px solid ${cfg.color}`,
              boxShadow: `0 0 24px -2px ${cfg.glow}`,
              animation: 'sim360-hub-pulse 2.4s ease-in-out infinite',
            }}
          >
            {cfg.logo
              ? <img src={cfg.logo} alt={cfg.label} className="w-14 h-14 rounded-full object-cover" />
              : <span style={{ color: cfg.color, fontWeight: 900, fontSize: 22 }}>{cfg.label?.[0]}</span>}
          </div>
          <div className="flex-1">
            <div className="text-2xl font-black tracking-tight"
              style={{ color: cfg.color, textShadow: `0 0 12px ${cfg.glow}` }}>
              {cfg.label}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#9EB4C0]">
              {ready ? `${trades} trades · live` : 'syncing data…'}
            </div>
            <div className="text-[9px] text-[#556475] font-mono mt-0.5">{server} · #{login}</div>
          </div>
        </div>

        {/* Audit transparency badge */}
        {auditLabel && (
          <div
            className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full"
            style={{
              background: 'rgba(0,244,255,0.06)',
              border: '1px solid rgba(0,244,255,0.25)',
            }}
            title={`Test trades before ${auditLabel} are excluded from these stats.`}
            data-testid={`audit-badge-${bot}`}
          >
            <ShieldCheck className="w-3 h-3 text-[#00f4ff]" />
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#00f4ff] font-bold">
              Audited since {auditLabel}
            </span>
          </div>
        )}
        {/* MyFxBook verification link */}
        {myfxbookUrl && (
          <a
            href={myfxbookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mb-3 ml-1.5 px-2.5 py-1 rounded-full hover:brightness-125 transition"
            style={{
              background: 'rgba(59,130,246,0.10)',
              border: '1px solid rgba(59,130,246,0.4)',
            }}
            title="Verify full trading history on MyFxBook (public)"
            data-testid={`myfxbook-link-${bot}`}
          >
            <ExternalLink className="w-3 h-3 text-[#60a5fa]" />
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#60a5fa] font-bold">
              Verify on MyFxBook
            </span>
          </a>
        )}

        {/* Big stat: profit / monthly gain */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="rounded-lg p-2.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${cfg.color}22` }}>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#556475]">Total Profit</div>
            <div className={`text-xl font-black font-mono ${profit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
              {ready ? (profit >= 0 ? '+' : '') + fmtUsdShort(profit) : '—'}
            </div>
            {mult !== 1 && <div className="text-[8px] text-[#556475] italic">cent → USD</div>}
          </div>
          <div className="rounded-lg p-2.5"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${cfg.color}22` }}>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#556475]">Monthly Gain</div>
            <div className={`text-xl font-black font-mono ${monthlyGain >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
              {ready ? (monthlyGain >= 0 ? '+' : '') + fmtPct(monthlyGain) : '—'}
            </div>
          </div>
        </div>

        {/* Equity curve */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-[9px] tracking-[0.3em] uppercase text-[#556475] mb-1">
            <span>Equity curve</span>
            <span className="text-[#556475] italic normal-case tracking-normal">{ready ? 'last 60 pts' : 'awaiting sync'}</span>
          </div>
          <EquityCurve analytics={analytics} login={login} color={cfg.color} glow={cfg.glow} animateIn={animateIn} />
        </div>

        {/* 4 KPI grid */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <Kpi label="WIN RATE"  value={ready ? fmtPct(winRate) : '—'} accent={cfg.color} />
          <Kpi
            label="PROFIT F."
            value={ready ? (pfAllWins ? '∞' : (pfPending ? '—' : fmtNum(pf, 2))) : '—'}
            accent={pf >= 1.5 || pfAllWins ? '#22c55e' : cfg.color}
            title={pfAllWins ? 'All trades won — Profit Factor is mathematically infinite' : (pfPending ? 'MetaStats still calculating — check MyFxBook for full-history value' : null)}
          />
          <Kpi
            label="SHARPE"
            value={ready ? (sharpePending ? '—' : fmtNum(sharpe, 2)) : '—'}
            accent={sharpe >= 1 ? '#22c55e' : cfg.color}
            title={sharpePending ? 'Sharpe ratio requires ≥30 days of daily equity — calibrating' : null}
          />
          <Kpi
            label="MAX DD"
            value={ready ? (ddPending ? '—' : fmtPct(dd)) : '—'}
            accent={dd > 15 ? '#ef4444' : cfg.color}
            title={ddPending ? 'Max drawdown still being computed by MetaStats' : null}
          />
        </div>

        {/* Long/Short donut + Streak */}
        {ready && (
          <>
            <div className="rounded-lg p-3 mb-2" style={{ background: 'rgba(0,0,0,0.30)', border: `1px solid ${cfg.color}22` }}>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#556475] mb-2">Trade Direction</div>
              <LongShortDonut analytics={analytics} color={cfg.color} glow={cfg.glow} />
            </div>
            <StreakBanner analytics={analytics} color={cfg.color} glow={cfg.glow} />
          </>
        )}

        {!ready && (
          <div className="rounded-lg p-3 text-center"
            style={{ background: 'rgba(0,244,255,0.06)', border: '1px solid rgba(0,244,255,0.25)' }}>
            <div className="text-[11px] text-[#00f4ff] font-bold mb-1">
              <Sparkles className="w-3 h-3 inline mr-1" />
              MetaStats is syncing
            </div>
            <p className="text-[10px] text-[#9EB4C0]">
              Real-time stats appear within 1–24h of first activation. Capital values are always hidden — only ratios are public.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Kpi = ({ label, value, accent, title }) => (
  <div className="rounded p-1.5" title={title || undefined}
    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${accent}22`, cursor: title ? 'help' : 'default' }}>
    <div className="text-[7px] tracking-[0.2em] uppercase text-[#556475]">{label}</div>
    <div className="text-xs font-bold tabular-nums font-mono mt-0.5" style={{ color: accent }}>
      {value}
    </div>
  </div>
);

/* ═════════════════════════════════════════════════════════════════ */
/*                LANZAMIENTO 2 — DEEP ANALYTICS WIDGETS              */
/* ═════════════════════════════════════════════════════════════════ */

/* Defensive extractors — MetaStats field shape varies across accounts.
   We try the canonical name first then sensible fallbacks. */
const extractSymbols = (analytics) => {
  if (!analytics) return [];
  // Try array forms first
  const arr = analytics.pairs || analytics.symbols || analytics.instruments;
  if (Array.isArray(arr) && arr.length) return arr;
  // Try dict form: {EURUSD: {profit, trades, ...}, ...}
  const dict = analytics.tradesByPair || analytics.pairsStats;
  if (dict && typeof dict === 'object') {
    return Object.entries(dict).map(([symbol, v]) => ({ symbol, ...(v || {}) }));
  }
  return [];
};

const extractMonthly = (analytics) => {
  if (!analytics) return [];
  const arr = analytics.monthlyAnalytics || analytics.monthlyGains || analytics.monthly;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((m) => {
      const period = m.period || {};
      return {
        year: period.year ?? m.year,
        month: period.month ?? m.month,
        gain: Number(m.gain ?? m.gainPercent ?? m.profitPercent ?? 0),
        profit: Number(m.profit ?? 0),
        trades: Number(m.trades ?? 0),
      };
    })
    .filter((x) => x.year && x.month);
};

const MONTH_LABELS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const EmptyDeep = ({ message, color }) => (
  <div className="rounded-lg p-4 text-center"
    style={{ background: 'rgba(0,244,255,0.04)', border: `1px dashed ${color || '#00f4ff'}33` }}>
    <Sparkles className="w-5 h-5 mx-auto mb-2" style={{ color: color || '#00f4ff' }} />
    <p className="text-[11px] text-[#9EB4C0] leading-relaxed">{message}</p>
  </div>
);

/* ─────────────── 1. SYMBOL PERFORMANCE HEATMAP ─────────────── */
const SymbolHeatmap = ({ analytics, color }) => {
  const items = useMemo(() => {
    const raw = extractSymbols(analytics);
    return raw
      .map((s) => ({
        symbol: String(s.symbol || s.name || '—').toUpperCase().slice(0, 8),
        profit: Number(s.profit || 0),
        trades: Number(s.trades || s.tradesCount || 0),
        won: Number(s.won || s.wonTrades || 0),
      }))
      .filter((s) => s.trades > 0)
      .sort((a, b) => Math.abs(b.profit) - Math.abs(a.profit))
      .slice(0, 8);
  }, [analytics]);

  if (items.length === 0) {
    return <EmptyDeep color={color} message="Symbol analytics will populate once MetaStats has indexed the first batch of historical trades (≤ 24h)." />;
  }
  const maxAbs = Math.max(...items.map((x) => Math.abs(x.profit)), 1);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {items.map((it) => {
        const intensity = Math.min(1, Math.abs(it.profit) / maxAbs);
        const win = it.profit >= 0;
        const baseColor = win ? '34,197,94' : '239,68,68';
        return (
          <div
            key={it.symbol}
            className="rounded-lg p-2.5 relative overflow-hidden transition-transform hover:scale-[1.04]"
            style={{
              background: `linear-gradient(135deg, rgba(${baseColor},${0.10 + intensity * 0.35}) 0%, rgba(${baseColor},${0.03 + intensity * 0.10}) 100%)`,
              border: `1px solid rgba(${baseColor},${0.30 + intensity * 0.50})`,
              boxShadow: `0 0 ${10 + intensity * 14}px -4px rgba(${baseColor},${0.25 + intensity * 0.45})`,
            }}
            data-testid={`symbol-tile-${it.symbol}`}
          >
            <div className="text-[9px] tracking-[0.2em] uppercase text-[#9EB4C0]">{it.symbol}</div>
            <div className={`text-sm font-black font-mono mt-1 ${win ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
              {win ? '+' : ''}{fmtUsdShort(it.profit)}
            </div>
            <div className="text-[8px] text-[#556475] mt-0.5 tracking-wider">
              {it.trades} TR · {it.trades > 0 ? fmtPct((it.won / it.trades) * 100) : '—'} WR
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─────────────── 2. RISK GAUGES (3D radial gauges) ─────────────── */
const RadialGauge = ({ label, value, max, color, isLowerBetter, suffix = '' }) => {
  const safeMax = max || 1;
  const pct = Math.max(0, Math.min(1, Math.abs(value) / safeMax));
  const ARC = 270; // open at bottom
  const CIRC = 2 * Math.PI * 36;
  const sweep = (ARC / 360) * CIRC;
  const filled = pct * sweep;
  // Healthy / warning / danger zones
  const healthy = isLowerBetter ? pct < 0.4 : pct > 0.6;
  const danger  = isLowerBetter ? pct > 0.7 : pct < 0.3;
  const trackColor = danger ? '#ef4444' : healthy ? '#22c55e' : color;

  return (
    <div className="relative flex flex-col items-center">
      <svg viewBox="0 0 90 90" width="100" height="100" className="overflow-visible">
        <defs>
          <linearGradient id={`gauge-${label}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={trackColor} stopOpacity="0.95" />
            <stop offset="100%" stopColor={trackColor} stopOpacity="0.55" />
          </linearGradient>
          <filter id={`gauge-glow-${label}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Track */}
        <circle cx="45" cy="45" r="36" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6"
          strokeDasharray={`${sweep} ${CIRC}`} strokeDashoffset={CIRC * 0.625}
          transform="rotate(135 45 45)" strokeLinecap="round" />
        {/* Filled arc */}
        <circle cx="45" cy="45" r="36" fill="none" stroke={`url(#gauge-${label})`} strokeWidth="6"
          strokeDasharray={`${filled} ${CIRC}`} strokeDashoffset={CIRC * 0.625}
          transform="rotate(135 45 45)" strokeLinecap="round"
          filter={`url(#gauge-glow-${label})`}
          style={{ transition: 'stroke-dasharray 1.4s ease-out' }} />
        {/* Tick marks for 3D feel */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const ang = ((135 + t * ARC) * Math.PI) / 180;
          const x1 = 45 + Math.cos(ang) * 30, y1 = 45 + Math.sin(ang) * 30;
          const x2 = 45 + Math.cos(ang) * 34, y2 = 45 + Math.sin(ang) * 34;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.18)" strokeWidth="0.8" />;
        })}
        <text x="45" y="46" textAnchor="middle" fontSize="13" fontWeight="900" fill={trackColor}
          fontFamily="ui-monospace, monospace"
          style={{ filter: `drop-shadow(0 0 4px ${trackColor})` }}>
          {fmtNum(value, value < 10 ? 2 : 1)}{suffix}
        </text>
        <text x="45" y="57" textAnchor="middle" fontSize="5" fill="#9EB4C0" letterSpacing="1.5">
          {label}
        </text>
      </svg>
    </div>
  );
};

const RiskGauges = ({ analytics, color }) => {
  if (!analytics) return null;
  const dd        = Number(analytics.maxDrawdownPercent || analytics.maxDrawdown || 0);
  const sortino   = Number(analytics.sortinoRatio || analytics.sharpeRatio || 0);
  let expectancy = Number(analytics.expectancy || 0);
  if (!expectancy && analytics.averageWin && analytics.averageLoss) {
    expectancy = (Number(analytics.averageWin) - Math.abs(Number(analytics.averageLoss))) / 2;
  }
  const hasData = dd > 0 || sortino !== 0 || expectancy !== 0;
  if (!hasData) {
    return <EmptyDeep color={color} message="Risk gauges (Max Drawdown · Sortino · Expectancy) will activate after a few closed trades are indexed." />;
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      <RadialGauge label="MAX DD%"  value={dd}        max={30} color={color} isLowerBetter suffix="%" />
      <RadialGauge label="SORTINO"  value={sortino}   max={3}  color={color} />
      <RadialGauge label="EXPECT."  value={expectancy} max={50} color={color} suffix="$" />
    </div>
  );
};

/* ─────────────── 3. MONTHLY RETURN CALENDAR ─────────────── */
const MonthlyCalendar = ({ analytics, color }) => {
  const months = useMemo(() => extractMonthly(analytics), [analytics]);

  if (months.length === 0) {
    return <EmptyDeep color={color} message="Monthly return calendar fills in once 1+ full month of trading data has been recorded." />;
  }

  // Group by year, find latest 2 years
  const byYear = {};
  months.forEach((m) => {
    if (!byYear[m.year]) byYear[m.year] = {};
    byYear[m.year][m.month] = m;
  });
  const years = Object.keys(byYear).map(Number).sort((a, b) => b - a).slice(0, 2);

  const allGains = months.map((m) => m.gain).filter((g) => Number.isFinite(g));
  const maxAbs = Math.max(...allGains.map(Math.abs), 1);

  const cellColor = (g) => {
    if (g === null || g === undefined) return 'rgba(255,255,255,0.02)';
    const intensity = Math.min(1, Math.abs(g) / maxAbs);
    const base = g >= 0 ? '34,197,94' : '239,68,68';
    return `rgba(${base},${0.10 + intensity * 0.55})`;
  };

  // Best/worst month
  const sorted = [...months].sort((a, b) => b.gain - a.gain);
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];

  return (
    <div className="space-y-3">
      {/* Calendar grid */}
      <div className="space-y-2">
        {years.map((y) => (
          <div key={y} className="flex items-center gap-2">
            <div className="text-[10px] font-mono text-[#9EB4C0] w-9 tracking-wider">{y}</div>
            <div className="grid grid-cols-12 gap-1 flex-1">
              {Array.from({ length: 12 }, (_, i) => {
                const m = byYear[y]?.[i + 1];
                const filled = m && Number.isFinite(m.gain);
                return (
                  <div
                    key={i}
                    title={filled ? `${MONTH_LABELS[i]} ${y}: ${fmtPct(m.gain)} · ${m.trades} trades` : `${MONTH_LABELS[i]} ${y} — no data`}
                    className="aspect-square rounded flex flex-col items-center justify-center transition-transform hover:scale-[1.10] cursor-default"
                    style={{
                      background: cellColor(filled ? m.gain : null),
                      border: `1px solid ${filled ? (m.gain >= 0 ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)') : 'rgba(255,255,255,0.06)'}`,
                    }}
                  >
                    <div className="text-[7px] text-[#9EB4C0] tracking-wider">{MONTH_LABELS[i]}</div>
                    {filled && (
                      <div className={`text-[8px] font-black font-mono leading-none ${m.gain >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                        {m.gain >= 0 ? '+' : ''}{m.gain.toFixed(1)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Best / Worst stripe */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg p-2 flex items-center gap-2"
          style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.30)' }}>
          <Trophy className="w-3.5 h-3.5 text-[#22c55e]" />
          <div className="leading-tight">
            <div className="text-[8px] uppercase tracking-[0.2em] text-[#9EB4C0]">Best month</div>
            <div className="text-xs font-black text-[#22c55e] font-mono">
              {best ? `${MONTH_LABELS[best.month - 1]} ${best.year} · +${best.gain.toFixed(1)}%` : '—'}
            </div>
          </div>
        </div>
        <div className="rounded-lg p-2 flex items-center gap-2"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.30)' }}>
          <TrendingDown className="w-3.5 h-3.5 text-[#ef4444]" />
          <div className="leading-tight">
            <div className="text-[8px] uppercase tracking-[0.2em] text-[#9EB4C0]">Worst month</div>
            <div className="text-xs font-black text-[#ef4444] font-mono">
              {worst ? `${MONTH_LABELS[worst.month - 1]} ${worst.year} · ${worst.gain.toFixed(1)}%` : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── DEEP ANALYTICS PANEL (composes the 3 widgets) ─────────────── */
const DeepAnalyticsPanel = ({ bots }) => {
  // Bots with at least some trades — fallback to first bot if none
  const eligible = useMemo(() => bots.filter((b) => (b.analytics?.trades || 0) > 0), [bots]);
  const [userPick, setUserPick] = useState(null);
  // Derive selection: user choice wins, else first eligible, else first bot
  const selectedBot = userPick || eligible[0]?.bot || bots[0]?.bot || null;

  const current = bots.find((b) => b.bot === selectedBot);
  if (!current) return null;
  const cfg = BOT_CONFIG[current.bot] || { color: '#00f4ff', glow: 'rgba(0,244,255,0.5)', label: current.bot };
  const a = current.analytics;

  return (
    <section className="mt-10" data-testid="deep-analytics-panel">
      {/* Section heading */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#00f4ff] mb-1">// Deep Analytics</div>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight">
            Inside the machine.
            <span className="block text-base font-normal text-[#9EB4C0] mt-1">
              Symbol bias · Risk profile · Monthly cadence
            </span>
          </h2>
        </div>
        {/* Bot selector pills */}
        <div className="flex gap-2 flex-wrap" role="tablist">
          {bots.map((b) => {
            const c = BOT_CONFIG[b.bot] || {};
            const active = b.bot === selectedBot;
            return (
              <button
                key={b.bot}
                onClick={() => setUserPick(b.bot)}
                role="tab"
                aria-selected={active}
                className="px-4 py-2 rounded-full text-[11px] font-bold tracking-[0.2em] uppercase transition-all"
                style={{
                  background: active ? c.color : 'transparent',
                  color: active ? '#020817' : c.color || '#00f4ff',
                  border: `1px solid ${c.color || '#00f4ff'}66`,
                  boxShadow: active ? `0 0 22px -4px ${c.glow}` : 'none',
                }}
                data-testid={`deep-tab-${b.bot}`}
              >
                {c.label || b.bot}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3 widget cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Symbol heatmap */}
        <Card className="border"
          style={{
            background: `linear-gradient(165deg, ${cfg.color}10 0%, rgba(4,8,22,0.92) 60%)`,
            borderColor: `${cfg.color}44`,
            boxShadow: `0 14px 40px -22px ${cfg.glow}`,
          }}
          data-testid="widget-symbol-heatmap">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3Icon color={cfg.color} />
              <div className="text-[11px] tracking-[0.3em] uppercase text-white font-bold">Symbol heatmap</div>
            </div>
            <p className="text-[10px] text-[#9EB4C0] mb-3 leading-relaxed">
              Net P/L per instrument · top 8 by absolute impact
            </p>
            <SymbolHeatmap analytics={a} color={cfg.color} />
          </CardContent>
        </Card>

        {/* Risk gauges */}
        <Card className="border"
          style={{
            background: `linear-gradient(165deg, ${cfg.color}10 0%, rgba(4,8,22,0.92) 60%)`,
            borderColor: `${cfg.color}44`,
            boxShadow: `0 14px 40px -22px ${cfg.glow}`,
          }}
          data-testid="widget-risk-gauges">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAltIcon color={cfg.color} />
              <div className="text-[11px] tracking-[0.3em] uppercase text-white font-bold">Risk profile</div>
            </div>
            <p className="text-[10px] text-[#9EB4C0] mb-4 leading-relaxed">
              How safely is this bot dancing on the edge?
            </p>
            <RiskGauges analytics={a} color={cfg.color} />
            <div className="mt-3 grid grid-cols-3 gap-2 text-[8px] text-[#556475] tracking-wider uppercase text-center">
              <div>lower = safer</div>
              <div>higher = better</div>
              <div>higher = better</div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly calendar */}
        <Card className="border"
          style={{
            background: `linear-gradient(165deg, ${cfg.color}10 0%, rgba(4,8,22,0.92) 60%)`,
            borderColor: `${cfg.color}44`,
            boxShadow: `0 14px 40px -22px ${cfg.glow}`,
          }}
          data-testid="widget-monthly-calendar">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon color={cfg.color} />
              <div className="text-[11px] tracking-[0.3em] uppercase text-white font-bold">Monthly returns</div>
            </div>
            <p className="text-[10px] text-[#9EB4C0] mb-3 leading-relaxed">
              Calendar heatmap · best/worst month at a glance
            </p>
            <MonthlyCalendar analytics={a} color={cfg.color} />
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

/* Tiny inline icons (avoid extra imports) */
const BarChart3Icon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round">
    <line x1="4" y1="20" x2="4" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="20" y1="20" x2="20" y2="14" />
  </svg>
);
const ShieldAltIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const CalendarIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/* Synthetic preview data — activated with ?demo=1 so prospects (and preview env)
   can see exactly what the page looks like once MetaStats sync completes. */
const buildDemoBots = () => {
  const mkMonths = (seed, bias = 1) => {
    const r = mulb(seedHash(seed));
    const out = [];
    const now = new Date();
    for (let i = 17; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({
        period: { year: d.getFullYear(), month: d.getMonth() + 1 },
        gain: (r() * 18 - 5) * bias,
        profit: (r() * 8000 - 1500) * bias,
        trades: Math.floor(r() * 120) + 20,
      });
    }
    return out;
  };
  const mkPairs = (seed, syms, bias = 1) => {
    const r = mulb(seedHash(seed));
    return syms.map((s) => {
      const t = Math.floor(r() * 250) + 30;
      const won = Math.floor(t * (0.45 + r() * 0.25));
      return { symbol: s, profit: (r() * 9000 - 2000) * bias, trades: t, won };
    });
  };
  return [
    {
      bot: 'sigmai', login: '1100204', server: 'CGFintechHK-Live', currency_multiplier: 1,
      audit_start_date: null,
      analytics: {
        trades: 842, wonTrades: 521, wonTradesPercent: 61.9, profit: 28430, monthlyGain: 4.8,
        profitFactor: 1.82, sharpeRatio: 1.34, sortinoRatio: 1.91, maxDrawdownPercent: 11.4,
        longTrades: 420, shortTrades: 422, longWonTradesPercent: 64.2, shortWonTradesPercent: 59.6,
        longestWinningStreak: 14, longestLosingStreak: 5,
        averageWin: 145, averageLoss: -82, expectancy: 31.5,
        monthlyAnalytics: mkMonths('sigmai-mo'),
        pairs: mkPairs('sigmai-pair', ['EURUSD','XAUUSD','BTCUSD','GBPJPY','US30','NAS100','GOLD']),
      },
    },
    {
      bot: 'sugair', login: '1200656', server: 'CGFintechHK-Live', currency_multiplier: 0.01,
      audit_start_date: '2026-03-01',
      analytics: {
        trades: 1240, wonTrades: 798, wonTradesPercent: 64.4, profit: 18920, monthlyGain: 6.2,
        profitFactor: 2.14, sharpeRatio: 1.56, sortinoRatio: 2.21, maxDrawdownPercent: 8.7,
        longTrades: 720, shortTrades: 520, longWonTradesPercent: 67.1, shortWonTradesPercent: 60.8,
        longestWinningStreak: 18, longestLosingStreak: 4,
        averageWin: 92, averageLoss: -48, expectancy: 28.4,
        monthlyAnalytics: mkMonths('sugair-mo', 1.15),
        pairs: mkPairs('sugair-pair', ['XAUUSD','EURUSD','USDJPY','BTCUSD','GBPUSD','SPX500']),
      },
    },
    {
      bot: 'jairvis', login: '1200654', server: 'CGFintechHK-Live', currency_multiplier: 0.01,
      audit_start_date: '2026-03-01',
      myfxbook_url: 'https://www.myfxbook.com/members/tradingwithai/jairvis-cg/11974324',
      analytics: {
        trades: 612, wonTrades: 348, wonTradesPercent: 56.9, profit: -3210, monthlyGain: -1.4,
        profitFactor: 0.92, sharpeRatio: 0.41, sortinoRatio: 0.58, maxDrawdownPercent: 22.6,
        longTrades: 310, shortTrades: 302, longWonTradesPercent: 58.4, shortWonTradesPercent: 55.3,
        longestWinningStreak: 7, longestLosingStreak: 11,
        averageWin: 64, averageLoss: -78, expectancy: -4.8,
        monthlyAnalytics: mkMonths('jairvis-mo', 0.6),
        pairs: mkPairs('jairvis-pair', ['BTCUSD','ETHUSD','XAUUSD','EURUSD','NAS100']),
      },
    },
  ];
};

/* ─────────────────────── PAGE ─────────────────────── */
const BotPerformanceHub = () => {
  // ?demo=1 → render synthetic data for marketing/previews
  const isDemo = (() => {
    try { return new URLSearchParams(window.location.search).get('demo') === '1'; }
    catch { return false; }
  })();

  const [bots, setBots] = useState(() => (isDemo ? buildDemoBots() : []));
  const [loading, setLoading] = useState(!isDemo);
  const [animateIn, setAnimateIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDemo) {
      apiFetch(`${API}/ai-bots/core-stats`)
        .then((r) => r.json())
        .then((d) => setBots(d?.bots || []))
        .catch(() => setBots([]))
        .finally(() => setLoading(false));
    }
    const t = setTimeout(() => setAnimateIn(true), 300);
    return () => clearTimeout(t);
  }, [isDemo]);

  const sorted = useMemo(() => {
    // Keep deterministic display order (Sigmai · Sugair · Jairvis) — no leaderboard ranking.
    const ORDER = { sigmai: 0, sugair: 1, jairvis: 2 };
    return [...bots].sort((a, b) => (ORDER[a.bot] ?? 99) - (ORDER[b.bot] ?? 99));
  }, [bots]);

  return (
    <div className="min-h-screen text-white"
      style={{
        backgroundColor: '#020817',
        backgroundImage:
          'radial-gradient(900px 500px at 20% 0%, rgba(0,244,255,0.12), transparent 60%), radial-gradient(700px 400px at 100% 100%, rgba(124,58,237,0.10), transparent 60%)',
      }}
      data-testid="bot-performance-hub"
    >
      {/* Hero */}
      <section className="px-4 lg:px-8 pt-8 pb-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {LOGO_SRC && <img src={LOGO_SRC} alt="DCA" className="h-8 w-auto" />}
            <span className="w-px h-6 bg-white/10" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#00f4ff]">Bot Performance Hub</span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-white/15 text-white hover:bg-white/5 rounded-full text-xs h-9"
            data-testid="hub-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Home
          </Button>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-4">
          Three engines, one mission.
          <span className="block mt-1 text-2xl sm:text-3xl bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(90deg, #ff3030 0%, #22c55e 50%, #00f4ff 100%)',
            }}>
            Sigmai · Sugair · Jairvis
          </span>
        </h1>
        <p className="text-[#9EB4C0] max-w-2xl text-sm sm:text-base mb-4">
          Three complementary AI cores running side-by-side — different styles, different timeframes, one diversified portfolio.
          Live analytics streamed from official MetaApi accounts. Capital figures are always hidden; only ratios. <strong className="text-white">Not a competition — a collaboration.</strong>
        </p>

        <div className="flex flex-wrap gap-3 mb-2">
          <Button
            onClick={() => { window.location.href = '/myportafolio?mode=register'; }}
            className="bg-gradient-to-r from-[#0051ff] to-[#00e5ff] text-[#020817] font-bold h-11 rounded-full px-6 shadow-[0_0_22px_-4px_rgba(0,114,255,0.75)] hover:brightness-110"
            data-testid="hub-cta-signup"
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Join The DCA Club
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-[#00f4ff]/40 text-[#00f4ff] hover:bg-[rgba(0,244,255,0.08)] h-11 rounded-full px-6"
            data-testid="hub-cta-sim"
          >
            <Activity className="w-4 h-4 mr-2" /> Open Simulator
          </Button>
        </div>
        <div className="text-[10px] text-[#556475] tracking-[0.2em] uppercase">
          ● live · powered by MetaApi.cloud · capital hidden
        </div>
      </section>

      {/* Cards grid */}
      <section className="px-4 lg:px-8 pb-12 max-w-6xl mx-auto">
        {loading && (
          <div className="text-center py-20 text-[#9EB4C0] text-sm">Loading live performance…</div>
        )}
        {!loading && bots.length === 0 && (
          <Card className="bg-[#040816] border-[#0b1c38] max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <Sparkles className="w-10 h-10 text-[#00f4ff] mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">Coming soon</h3>
              <p className="text-sm text-[#9EB4C0]">
                The Core bot accounts are being provisioned. Stats will appear here once activated.
              </p>
            </CardContent>
          </Card>
        )}
        {!loading && bots.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sorted.map((b) => (
              <BotHubCard
                key={b.bot}
                bot={b.bot}
                analytics={b.analytics}
                login={b.login}
                server={b.server}
                mult={b.currency_multiplier}
                auditStartDate={b.audit_start_date}
                myfxbookUrl={b.myfxbook_url}
                animateIn={animateIn}
              />
            ))}
          </div>
        )}

        {/* LANZAMIENTO 2 — Deep Analytics (Symbol Heatmap · Risk Gauges · Monthly Calendar) */}
        {!loading && bots.length > 0 && (
          <DeepAnalyticsPanel bots={sorted} />
        )}
      </section>

      {/* Footer */}
      <footer className="px-4 lg:px-8 pb-10 max-w-6xl mx-auto text-center">
        <p className="text-[10px] text-[#556475] tracking-[0.25em] uppercase">
          The DCA Club · sim360° · educational use only · past performance is not indicative of future results
        </p>
      </footer>

      <style>{`
        @keyframes sim360-hub-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default BotPerformanceHub;
